import { useState, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";

// Components
import Modal from "components/organism/modal-v2";
import Button from "components/v1/Button";
import FormCustInfo from "components/atomic/buyer-match/form-cust-info";
import FormLokasiDanProperti from "components/atomic/buyer-match/form-lokasi-dan-properti";
import FormDetailDanBudget from "components/atomic/buyer-match/form-detail-dan-budget";
import ModalPilihLokasi from "components/molecules/buyer-match/modal-pilih-lokasi/modal-pilih-lokasi";
import { useGetUnitPrice } from "api/common/query";
import ModalPilihCodeNegara from "components/molecules/buyer-match/modal-pilih-code-negara/modal-pilih-code-negara";

// Assets
import { ReactComponent as XIcon } from "assets/cross.svg";
import IArrow from "assets/i-arrow.js";
import IArrowShortail from "assets/i-arrow-shortail.js";

const Progress = ["Customer Info", "Lokasi & Properti", "Detail & Budget"];

const ModalEditBuyerAgent = ({
  onSubmit,
  defaultValues,
  options,
  onClose,
  isOpen,
  ...props
}) => {
  const [isModalPilihLokasiOpen, setIsModalPilihLokasiOpen] = useState(false);
  const [isModalPilihCodeNegaraOpen, setIsModalPilihCodeNegaraOpen] =
    useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const schema = Yup.object().shape({
    name: Yup.string().required("Nama wajib diisi"),
    whatsapp_number: Yup.string().required("Nomor WhatsApp wajib diisi"),
    listing_type_id: Yup.number().required("Tipe Listing wajib diisi"),
    property_type_id: Yup.number().required("Tipe Property wajib diisi"),
    min_surface_area: Yup.string().optional(),
    min_building_area: Yup.string().optional(),
    min_price: Yup.string().required("Harga Minimum wajib diisi"),
    max_price: Yup.string().required("Harga Maximum wajib diisi"),
    bedroom: Yup.string().optional(),
    bathroom: Yup.string().optional(),
    is_archive: Yup.boolean(),
    is_studio: Yup.boolean(),
    unit_price_id: Yup.number().nullable(),
    preferred_area_ids: Yup.array().min(1, "Area Preferensi wajib diisi"),
    dial_code: Yup.string().required("Kode Negara wajib diisi"),
  });

  const { control, handleSubmit, watch, setValue } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: defaultValues,
  });

  const { data: unitPriceData } = useGetUnitPrice({
    params: {
      property_type_id: 2,
    },
    enabled: watch("listing_type_id") === 2,
  });

  const optionUnitPrice = useMemo(() => {
    if (!unitPriceData?.data?.data?.length) return [];
    return unitPriceData.data.data.map((item) => ({
      value: item.id,
      label: item.name,
    }));
  }, [unitPriceData]);

  const handleNextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSelectLocation = (id) => {
    let newValues = [...watch("preferred_area_ids")];
    if (newValues.includes(id)) {
      newValues = newValues.filter((item) => item !== id);
    } else {
      newValues.push(id);
    }
    setValue("preferred_area_ids", newValues);
  };

  const handleApplySelectedLocation = (locations = []) => {
    let newValues = [...watch("preferred_area_ids")];
    newValues = [...locations];
    setValue("preferred_area_ids", newValues);
  };

  const handleApplySelectedCodeNegara = (dialCode = "") => {
    setValue("dial_code", dialCode);
    setIsModalPilihCodeNegaraOpen(false);
  };

  const allWatch = watch();
  const disableBtnNext = useMemo(() => {
    if (currentStep === 0) {
      return !allWatch.name?.trim() || !allWatch.whatsapp_number?.trim();
    }
    if (currentStep === 1) {
      return (
        !allWatch.listing_type_id ||
        !allWatch.property_type_id ||
        !allWatch.preferred_area_ids?.length
      );
    }
    return false;
  }, [currentStep, allWatch]);

  const disableBtnSubmit = useMemo(() => {
    let result = false;
    if (currentStep === 2) {
      result = !allWatch.min_price?.trim() || !allWatch.max_price?.trim();

      if (watch("listing_type_id") === 2) {
        result = result || !allWatch.unit_price_id;
      }
    }
    return result;
  }, [currentStep, allWatch]);

  const _renderContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <FormLokasiDanProperti
            onSelect={handleSelectLocation}
            selectedLocations={watch("preferred_area_ids")}
            onOpenModalPilihLokasi={() => setIsModalPilihLokasiOpen(true)}
            control={control}
            Controller={Controller}
            options={options}
          />
        );
      case 2:
        return (
          <FormDetailDanBudget
            listing_type_id={watch("listing_type_id")}
            optionUnitPrice={optionUnitPrice}
            property_type_id={watch("property_type_id")}
            control={control}
            Controller={Controller}
          />
        );
      default:
        return (
          <FormCustInfo
            onOpenModalPilihCodeNegara={() =>
              setIsModalPilihCodeNegaraOpen(true)
            }
            control={control}
            Controller={Controller}
            dial_code={watch("dial_code")}
          />
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} {...props}>
      <div
        className="w-[426px] z-[42] bg-white rounded-md border-[0.5px] border-solid border-[#ECECEC] 
        shadow-[0px_1px_3px_0px_rgba(16,16,16,0.10),_0px_1px_2px_0px_rgba(16,16,16,0.06)]"
      >
        <div className="flex justify-between px-4 py-2 border-b border-solid border-[#D8D8D9]">
          <p className="text-[17px] font-inter-semibold">
            Ubah Profil Customer
          </p>
          <XIcon onClick={onClose} />
        </div>

        <div className="flex justify-center gap-1 items-center px-4 py-2 border-y border-solid border-[#D8D8D9]">
          {Progress.map((item, index) => {
            const isActive = index === currentStep;

            return (
              <div
                key={`progress-${index}`}
                className="flex items-center gap-1"
              >
                {index > 0 && <IArrow />}
                <div className="flex items-center gap-0.5">
                  <div
                    className={`w-4 h-4 flex items-center justify-center rounded-[2px] ${
                      isActive ? "bg-[#BEAF87]" : "bg-[#D8D8D9]"
                    } text-[11px] tracking-[-0.4px] font-inter-semibold text-[#252526]`}
                  >
                    {index + 1}
                  </div>
                  <span
                    className={`text-[11px] tracking-[-0.4px] font-inter-semibold ${
                      isActive ? "text-[#252526]" : "text-[#808285]"
                    }`}
                  >
                    {item}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="px-4 py-2 h-[374px] overflow-y-auto">
            {_renderContent()}
          </div>

          <div
            className={
              "flex items-center justify-end gap-4 px-4 py-2 border-t border-solid border-[#D8D8D9]"
            }
          >
            {currentStep === 1 && (
              <Button
                icon={IArrowShortail}
                onClick={handlePrevStep}
                text="1. Customer Info"
                type="secondary"
              />
            )}
            {currentStep === 2 && (
              <Button
                icon={IArrowShortail}
                onClick={handlePrevStep}
                text="2. Lokasi & Properti"
                type="secondary"
              />
            )}
            {currentStep < 2 && (
              <Button
                icon={IArrowShortail}
                rotateIcon="180"
                iconPosition="right"
                iconColor="#fff"
                disabled={disableBtnNext}
                onClick={handleNextStep}
                text={
                  currentStep === 0
                    ? "2. Lokasi & Properti"
                    : "3. Detail & Budget"
                }
              />
            )}
            {currentStep === 2 && (
              <Button
                disabled={disableBtnSubmit}
                btnPrimitiveType="submit"
                text="Simpan Perubahan"
              />
            )}
          </div>
        </form>

        <ModalPilihLokasi
          onSelect={handleApplySelectedLocation}
          selectedValues={watch("preferred_area_ids")}
          isOpen={isModalPilihLokasiOpen}
          onClose={() => setIsModalPilihLokasiOpen(false)}
        />
        <ModalPilihCodeNegara
          onSelect={handleApplySelectedCodeNegara}
          selectedValue={watch("dial_code")}
          isOpen={isModalPilihCodeNegaraOpen}
          onClose={() => setIsModalPilihCodeNegaraOpen(false)}
        />
      </div>
    </Modal>
  );
};

const Wrapper = ({ isOpen, ...props }) => {
  if (!isOpen) return null;
  return <ModalEditBuyerAgent isOpen={isOpen} {...props} />;
};

export default Wrapper;
